import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

// ─── Types ───────────────────────────────────────────────────────────────────

export type UploadType = 'demographics' | 'geographic' | 'financial' | 'insurance';

export interface PracticeUpload {
  id: string;
  locationId: string;
  uploadType: UploadType;
  fileName: string;
  fileSize: number;
  mimeType: string;
  status: 'processing' | 'success' | 'partial' | 'failed';
  parsedData: unknown;
  parseErrors: string[] | null;
  summary: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DetailedCensusData {
  gender: {
    malePct: number;
    femalePct: number;
  };
  education: {
    noDegree: number;
    highSchool: number;
    someCollege: number;
    bachelors: number;
    postGrad: number;
  };
  income: {
    under50k: number;
    from50kTo100k: number;
    from100kTo200k: number;
    over200k: number;
    medianHouseholdIncome: number | null;
  };
  maritalStatus: {
    neverMarriedMale: number;
    neverMarriedFemale: number;
    nowMarriedMale: number;
    nowMarriedFemale: number;
    divorcedMale: number;
    divorcedFemale: number;
    widowedMale: number;
    widowedFemale: number;
  };
  householdStructure: {
    familyHouseholds: number;
    marriedCoupleFamily: number;
    singleParentFamily: number;
    nonfamilyHouseholds: number;
  };
  supplemental: {
    totalPopulation: number;
    medianHomeValue: number | null;
    housingOwners: number;
    housingRenters: number;
    raceWhite: number;
    raceHispanic: number;
    raceAsian: number;
    raceBlack: number;
    raceTwoOrMore: number;
  };
  language: {
    englishOnly: number;
    spanish: number;
    asianPacificIslander: number;
    otherIndoEuropean: number;
    other: number;
  };
  healthInsurance: {
    insured: number;
    uninsured: number;
    uninsuredRate: number;
    privateEmployerEst: number;
    benefitCoverageEst: number;
  };
}

export interface CensusZip {
  zip: string;
  city: string;
  state: string;
  detailedData: DetailedCensusData | null;
}

export interface CensusData {
  zipCodes: CensusZip[];
  totalPopulation: number;
}

export interface DemographicsData {
  totalPatients: number;
  ageBrackets: Array<{ range: string; count: number }>;
  genderSplit: { male: number; female: number; other: number };
}

export interface GeographicData {
  zipCodes: Array<{ zip: string; patientCount: number }>;
  totalPatients: number;
}

export interface FinancialData {
  totalProduction: number;
  patientCount: number;
  avgSpend: number;
  medianSpend: number | null;
  procedureCategories: Array<{ name: string; revenue: number }>;
  timePeriods: Array<{ period: string; production: number }>;
}

export type OpportunityLevel = 'HIGH' | 'MEDIUM' | 'LOW';

export interface ZipScore {
  zip: string;
  score: number;
  level: OpportunityLevel;
}

export type CensusError = 'missing_zip' | 'invalid_zip' | 'fetch_failed' | 'partial';

export interface MarketShare {
  percent: number;
  patients: number;
  careSeekingPopulation: number;
  /** 'area' = patients from ZIPs in the radius; 'all' = every patient (no ZIP upload). */
  scope: 'area' | 'all';
}

export interface MarketIntelData {
  census: CensusData | null;
  censusError: CensusError | null;
  geographic: GeographicData | null;
  demographics: DemographicsData | null;
  financial: FinancialData | null;
  scores: Record<string, ZipScore>;
  hasPatientData: boolean;
  radius: number;
  missingZipCount: number;
  marketShare: MarketShare | null;
  /** Share of residents who seek this vertical's care in a year. */
  seekingRate: number;
}

export type EnrichmentLevel = 'basic' | 'enhanced' | 'full';

export interface EnrichmentData {
  level: EnrichmentLevel;
  uploadCount: number;
  uploads: Record<UploadType, { status: string; summary: string | null } | null>;
  pmsConnected: boolean;
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const uploadApi = {
  upload: (locationId: string, uploadType: UploadType, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('locationId', locationId);
    formData.append('uploadType', uploadType);
    return apiClient
      .post<ApiResponse<{ id: string; status: string; summary: string | null }>>(
        '/strategy/uploads',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          // The upload returns once the file is read; the AI extraction runs in
          // the background and the card polls the upload until it settles.
        },
      )
      .then((r) => r.data);
  },

  getUploads: (locationId: string) =>
    apiClient
      .get<ApiResponse<PracticeUpload[]>>('/strategy/uploads', { params: { locationId } })
      .then((r) => r.data),

  deleteUpload: (id: string) =>
    apiClient
      .delete<ApiResponse<{ deleted: boolean }>>(`/strategy/uploads/${id}`)
      .then((r) => r.data),

  getEnrichment: (locationId: string) =>
    apiClient
      .get<ApiResponse<EnrichmentData>>('/strategy/enrichment', { params: { locationId } })
      .then((r) => r.data),

  getMarketIntel: (locationId: string) =>
    apiClient
      .get<ApiResponse<MarketIntelData>>('/strategy/market-intel', { params: { locationId } })
      .then((r) => r.data),

  getRadius: (locationId: string) =>
    apiClient
      .get<
        ApiResponse<{
          radius: number;
          specialty: string;
          vertical: string;
          isSpecialist: boolean;
        }>
      >('/strategy/radius', { params: { locationId } })
      .then((r) => r.data),

  setRadius: (locationId: string, radius: number) =>
    apiClient
      .put<ApiResponse<{ radius: number }>>('/strategy/radius', { locationId, radius })
      .then((r) => r.data),
};
